import { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Stack, Switch, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation, Trans } from 'react-i18next';
import { webhooksApi } from '../../services/integrations';
import { formatDateTime } from '../../i18n/format';
import { DEFAULT_WEBHOOK_EVENTS, WEBHOOK_EVENTS } from '@traaker/shared';
import type { WebhookConfigView, WebhookDelivery, WebhookEvent } from '@traaker/shared';

const EVENT_LABEL_KEY: Record<WebhookEvent, string> = {
  'article.generated': 'webhooks.event.generated',
  'article.published': 'webhooks.event.published',
  'article.dismissed': 'webhooks.event.dismissed',
  'notification.global_report': 'webhooks.event.notif.globalReport',
  'notification.score_report': 'webhooks.event.notif.scoreReport',
  'notification.competitor_news': 'webhooks.event.notif.competitorNews',
  'notification.score_degradation': 'webhooks.event.notif.scoreDegradation',
  'notification.tasks_recos': 'webhooks.event.notif.tasksRecos',
  'notification.credit_alert': 'webhooks.event.notif.creditAlert',
  'notification.audit_failed': 'webhooks.event.notif.auditFailed',
};

export default function WebhooksIntegration() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState<WebhookConfigView[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setHooks(await webhooksApi.list()); } catch { /* no-op */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Webhooks</Typography>
          <Typography variant="body2" color="text.secondary">
            <Trans
              i18nKey="webhooks.intro"
              components={{ b: <b />, code: <code /> }}
            />
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ whiteSpace: 'nowrap' }}>{t('webhooks.add')}</Button>
      </Box>

      <WebhookPayloadHelp />

      {msg && <Alert severity="success" sx={{ my: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : hooks.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>{t('webhooks.empty')}</Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {hooks.map((h) => <WebhookRow key={h.id} hook={h} onChange={load} onMsg={setMsg} />)}
        </Stack>
      )}

      <AddWebhookDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
    </Box>
  );
}

const PAYLOAD_EXAMPLE = `POST  <votre URL>
X-Traaker-Event: article.generated
X-Traaker-Signature: sha256=<hmac du corps avec votre secret>

{
  "event": "article.generated",
  "articleId": "…", "brandId": "…",
  "occurredAt": "2026-07-05T…Z",
  "data": {
    "title": "…",
    "subtitle": "…",
    "articleMarkdown": "# … (article complet)",
    "variants": [
      { "channel": "linkedin_post", "kind": "announcement", "text": "…" },
      { "channel": "x_thread", "kind": "thread", "text": "[{…}]" }
    ],
    "targetGeoQuery": "…",
    "topicTags": ["…"],
    "editorUrl": "https://app.traaker.ai/news-watch/article/…"
  }
}`;

// The notification.* family (plan §13.1): same signature scheme, different body
// shape — `data` is whatever the renderer already computed for the email.
const NOTIFICATION_PAYLOAD_EXAMPLE = `POST  <votre URL>
X-Traaker-Event: notification.score_degradation
X-Traaker-Signature: sha256=<hmac du corps avec votre secret>

{
  "event": "notification.score_degradation",
  "tenantId": "…", "brandId": "…", "brandName": "…",
  "type": "score_degradation",
  "periodStart": "2026-08-14", "periodEnd": "2026-08-21",
  "occurredAt": "2026-08-21T06:00:12Z",
  "data": {
    "scope": "bp_overall",
    "currentScore": 61, "previousScore": 68,
    "thresholdPoints": 5, "consecutiveRuns": 3
  },
  "aiSummary": "…",
  "appUrl": "https://app.traaker.ai/brands/…"
}`;

function WebhookPayloadHelp() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', pl: 0 }}>
        {t('webhooks.messageStructure')}
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Trans
              i18nKey="webhooks.payloadHelp"
              components={{ b: <b />, code: <code /> }}
            />
          </Typography>
          <Box component="pre" sx={{
            m: 0, p: 1.5, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1,
            fontSize: 11.5, lineHeight: 1.5, overflowX: 'auto', fontFamily: 'ui-monospace, monospace',
          }}>{PAYLOAD_EXAMPLE}</Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            <Trans
              i18nKey="webhooks.notificationPayloadHelp"
              components={{ b: <b />, code: <code /> }}
            />
          </Typography>
          <Box component="pre" sx={{
            m: 0, p: 1.5, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1,
            fontSize: 11.5, lineHeight: 1.5, overflowX: 'auto', fontFamily: 'ui-monospace, monospace',
          }}>{NOTIFICATION_PAYLOAD_EXAMPLE}</Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function WebhookRow({ hook, onChange, onMsg }: { hook: WebhookConfigView; onChange: () => void; onMsg: (m: string) => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[] | null>(null);
  const [testing, setTesting] = useState(false);

  const toggleActive = async () => { await webhooksApi.update(hook.id, { active: !hook.active }).catch(() => {}); onChange(); };
  const remove = async () => { await webhooksApi.remove(hook.id).catch(() => {}); onChange(); };
  const test = async () => {
    setTesting(true);
    try { const r = await webhooksApi.test(hook.id); onMsg(r.ok ? t('webhooks.testSuccess') : t('webhooks.testFailed')); }
    finally { setTesting(false); if (expanded) loadDeliveries(); }
  };
  const loadDeliveries = async () => { setDeliveries(await webhooksApi.deliveries(hook.id).catch(() => [])); };
  const onExpand = () => { const next = !expanded; setExpanded(next); if (next && !deliveries) loadDeliveries(); };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {hook.url}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5, mt: .75 }}>
            {hook.events.map((e) => <Chip key={e} size="small" label={t(EVENT_LABEL_KEY[e])} />)}
            <Chip size="small" label={t('webhooks.secretChip', { hint: hook.secretHint })} variant="outlined" sx={{ fontFamily: 'ui-monospace, monospace' }} />
          </Box>
        </Box>
        <FormControlLabel control={<Switch checked={hook.active} onChange={toggleActive} size="small" />} label={hook.active ? t('webhooks.active') : t('webhooks.inactive')} />
        <IconButton size="small" onClick={test} disabled={testing} title={t('webhooks.sendTest')}>
          {testing ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={onExpand} title={t('webhooks.deliveryLog')}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" color="error" onClick={remove} title={t('webhooks.delete')}><DeleteOutlineIcon fontSize="small" /></IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{t('webhooks.recentDeliveries')}</Typography>
          {deliveries == null ? (
            <Box sx={{ py: 1 }}><CircularProgress size={16} /></Box>
          ) : deliveries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{t('webhooks.noDeliveries')}</Typography>
          ) : (
            <Stack spacing={.5} sx={{ mt: .5 }}>
              {deliveries.slice(0, 10).map((d, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5 }}>
                  <Chip size="small" label={d.status === 'success' ? t('webhooks.ok') : t('webhooks.fail')}
                    color={d.status === 'success' ? 'success' : 'error'} sx={{ height: 18 }} />
                  <span>{t(EVENT_LABEL_KEY[d.event])}</span>
                  <span style={{ color: '#888' }}>{d.responseCode ? `HTTP ${d.responseCode}` : d.error || ''}</span>
                  <span style={{ marginLeft: 'auto', color: '#888' }}>{formatDateTime(d.deliveredAt)}</span>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// Pre-check the content events only: subscribing to a `notification.*` event is
// an explicit choice (the per-type mute lives in the Notifications tab, so an
// auto-checked box here would silently start fanning reports out). Sourced
// from shared so the UI default and the server's own fallback (WebhooksService
// .validEvents) can never disagree.
const DEFAULT_EVENTS: WebhookEvent[] = DEFAULT_WEBHOOK_EVENTS;

function AddWebhookDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([...DEFAULT_EVENTS]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [createdSecret, setCreatedSecret] = useState('');

  const reset = () => { setUrl(''); setEvents([...DEFAULT_EVENTS]); setErr(''); setCreatedSecret(''); };
  const close = () => { reset(); onClose(); };

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const res = await webhooksApi.create({ url, events });
      setCreatedSecret(res.secret);
      onCreated();
    } catch (e: any) { setErr(e.message || t('webhooks.createFailed')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('webhooks.dialog.title')}</DialogTitle>
      <DialogContent>
        {createdSecret ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              {t('webhooks.dialog.secretWarning')}
            </Alert>
            <TextField label={t('webhooks.dialog.secretLabel')} value={createdSecret} fullWidth size="small"
              InputProps={{ readOnly: true, sx: { fontFamily: 'ui-monospace, monospace' } }} />
            <Button variant="contained" onClick={() => { navigator.clipboard.writeText(createdSecret).catch(() => {}); }}>{t('webhooks.dialog.copySecret')}</Button>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error">{err}</Alert>}
            <TextField label={t('webhooks.dialog.urlLabel')} placeholder="https://hooks.exemple.com/traaker" fullWidth size="small"
              value={url} onChange={(e) => setUrl(e.target.value)} helperText={t('webhooks.dialog.urlHelper')} />
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: .5 }}>{t('webhooks.dialog.events')}</Typography>
              {WEBHOOK_EVENTS.map((e) => (
                <FormControlLabel key={e}
                  control={<Switch size="small" checked={events.includes(e)}
                    onChange={() => setEvents((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e])} />}
                  label={t(EVENT_LABEL_KEY[e])} />
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {createdSecret ? (
          <Button onClick={close} variant="contained">{t('webhooks.dialog.done')}</Button>
        ) : (
          <>
            <Button onClick={close}>{t('webhooks.dialog.cancel')}</Button>
            <Button variant="contained" onClick={create} disabled={busy || !url || events.length === 0}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>{t('webhooks.dialog.create')}</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
